import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Banknote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { formatMoney, parseAmountToCents } from "@/lib/money";
import { PinDialog } from "@/components/PinDialog";

interface Withdrawal {
  id: string;
  amount_cents: number;
  status: string;
  bank_name: string | null;
  bank_account_last4: string | null;
  recipient_name: string | null;
  admin_notes: string | null;
  created_at: string;
}

const statusTone: Record<string, string> = {
  pending: "bg-muted text-foreground",
  approved: "bg-primary/15 text-primary",
  paid: "bg-emerald-500/15 text-emerald-600",
  rejected: "bg-destructive/15 text-destructive",
  on_hold: "bg-amber-500/15 text-amber-700",
};

export default function Withdraw() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balanceCents, setBalanceCents] = useState<number>(0);
  const [amount, setAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountFull, setAccountFull] = useState("");
  const [routingFull, setRoutingFull] = useState("");
  const [recipient, setRecipient] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [history, setHistory] = useState<Withdrawal[]>([]);

  const refresh = async () => {
    if (!user) return;
    const [{ data: w }, { data: h }] = await Promise.all([
      supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle(),
      supabase
        .from("withdrawals")
        .select("id, amount_cents, status, bank_name, bank_account_last4, recipient_name, admin_notes, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10),
    ]);
    if (w) setBalanceCents(w.balance_cents);
    if (h) setHistory(h as Withdrawal[]);
  };

  useEffect(() => {
    refresh();
  }, [user]);

  const cents = parseAmountToCents(amount);
  const accountLast4 = accountFull.replace(/\D/g, "").slice(-4);
  const routingLast4 = routingFull.replace(/\D/g, "").slice(-4);
  const canSubmit =
    !!cents && cents >= 100 && cents <= balanceCents &&
    bankName.trim().length >= 2 && accountLast4.length === 4 &&
    recipient.trim().length >= 2;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPinOpen(true);
  };

  const handlePin = async (pin: string) => {
    setPinOpen(false);
    if (!cents) return;
    setSubmitting(true);
    const { error } = await supabase.rpc("request_withdrawal", {
      _amount_cents: cents,
      _bank_name: bankName.trim(),
      _account_last4: accountLast4,
      _routing_last4: routingLast4 || null,
      _recipient_name: recipient.trim(),
      _notes: notes.trim() || null,
      _pin: pin,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Withdrawal failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Withdrawal requested", description: "We'll review and process it shortly." });
    setAmount(""); setAccountFull(""); setRoutingFull(""); setNotes("");
    refresh();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-3 px-4 py-4 border-b border-border">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-display font-bold text-lg">Withdraw</h1>
          <p className="text-xs text-muted-foreground">Send funds to your bank</p>
        </div>
      </header>

      <main className="px-4 py-5 max-w-md mx-auto space-y-5">
        <Card className="p-4 bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Available</p>
              <p className="font-display text-2xl font-bold">{formatMoney(balanceCents)}</p>
            </div>
            <Banknote className="h-8 w-8 text-primary" />
          </div>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="amt">Amount (USD)</Label>
            <Input id="amt" inputMode="decimal" placeholder="0.00" value={amount}
              onChange={(e) => setAmount(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">Min $1.00 · Max $5,000 / 24h</p>
          </div>
          <div>
            <Label htmlFor="rec">Recipient name</Label>
            <Input id="rec" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Full name on account" />
          </div>
          <div>
            <Label htmlFor="bank">Bank name</Label>
            <Input id="bank" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Chase, Wells Fargo, …" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="acct">Account #</Label>
              <Input id="acct" inputMode="numeric" value={accountFull}
                onChange={(e) => setAccountFull(e.target.value)} placeholder="•••• 1234" />
              <p className="text-xs text-muted-foreground mt-1">We store last 4 only</p>
            </div>
            <div>
              <Label htmlFor="rout">Routing #</Label>
              <Input id="rout" inputMode="numeric" value={routingFull}
                onChange={(e) => setRoutingFull(e.target.value)} placeholder="•••• 5678" />
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200} />
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={!canSubmit || submitting}>
            {submitting ? "Submitting…" : "Request withdrawal"}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            Funds are held immediately and released if rejected.
          </p>
        </form>

        <section>
          <h2 className="font-semibold text-sm mb-2">Recent withdrawals</h2>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground">No withdrawals yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((w) => (
                <Card key={w.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{formatMoney(w.amount_cents)}</p>
                    <p className="text-xs text-muted-foreground">
                      {w.bank_name} •••• {w.bank_account_last4} · {new Date(w.created_at).toLocaleDateString()}
                    </p>
                    {w.admin_notes && <p className="text-[11px] text-muted-foreground italic mt-1">{w.admin_notes}</p>}
                  </div>
                  <Badge className={statusTone[w.status] ?? ""} variant="secondary">{w.status}</Badge>
                </Card>
              ))}
            </div>
          )}
        </section>

        <p className="text-center">
          <Link to="/wallet" className="text-xs text-muted-foreground hover:underline">Back to wallet</Link>
        </p>
      </main>

      <PinDialog
        open={pinOpen}
        onCancel={() => setPinOpen(false)}
        onSubmit={handlePin}
        pending={submitting}
        title="Confirm withdrawal"
        description={cents ? `Withdraw ${formatMoney(cents)} to ${bankName} ••${accountLast4}` : ""}
      />
    </div>
  );
}
